import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CommandPalette } from './CommandPalette';
import { KeyboardShortcutsModal } from './KeyboardShortcutsModal';
import { registerOverlay, unregisterOverlay } from '@shared/lib/overlayRegistry';

const PALETTE_OVERLAY_ID = 'command-palette';

export type PaletteMode = 'find';

type CommandPaletteContextValue = {
  open: boolean;
  query: string;
  mode: PaletteMode;
  setOpen: (open: boolean) => void;
  setQuery: (query: string) => void;
  setMode: (mode: PaletteMode) => void;
  toggle: (mode?: PaletteMode) => void;
  /** Navigate to Smart Match intake with an optional pre-filled prompt. */
  openNewSearch: (promptText?: string) => void;
};

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error('useCommandPalette must be used within <CommandPaletteProvider>');
  }
  return ctx;
}

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpenState] = useState(false);
  const [query, setQuery] = useState('');
  const [mode, setModeState] = useState<PaletteMode>('find');
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const openerRef = useRef<HTMLElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const setOpen = useCallback((next: boolean) => {
    if (next) {
      openerRef.current = (document.activeElement as HTMLElement | null) ?? null;
    }
    setOpenState(next);
    if (!next) {
      setQuery('');
      setModeState('find');
    }
  }, []);

  const setMode = useCallback((m: PaletteMode) => {
    setModeState(m);
  }, []);

  /**
   * toggle(mode?) behaviour:
   *  - Called with explicit mode: open palette (if closed) in Find mode.
   *  - Called without mode: old open/close toggle behaviour.
   */
  const toggle = useCallback((requestedMode?: PaletteMode) => {
    if (requestedMode !== undefined) {
      setOpenState((o) => {
        if (!o) {
          openerRef.current = (document.activeElement as HTMLElement | null) ?? null;
        }
        return true;
      });
      setModeState(requestedMode);
    } else {
      setOpenState((o) => {
        const next = !o;
        if (next) {
          openerRef.current = (document.activeElement as HTMLElement | null) ?? null;
        } else {
          setQuery('');
          setModeState('find');
        }
        return next;
      });
    }
  }, []);

  /**
   * Navigate to Smart Match intake with an optional pre-filled prompt.
   * Kept under the `openNewSearch` name so existing callers (Home chips,
   * NextStepBridge, etc.) keep working.
   */
  const openNewSearch = useCallback((promptText = '') => {
    const trimmed = promptText.trim();
    const search = trimmed ? `?q=${encodeURIComponent(trimmed)}` : '';
    navigate(`/smart-match/new${search}`);
  }, [navigate]);

  // ⌘K / ⌘/ → Command Palette (Find mode only).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey) {
        if (e.key === 'k' || e.key === 'K' || e.key === '/') {
          e.preventDefault();
          toggle('find');
          return;
        }
        return;
      }
      // `?` key — ignore when typing in an input/textarea/select/contenteditable
      if (e.key === '?') {
        const active = document.activeElement as HTMLElement | null;
        const tag = active?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || active?.isContentEditable) return;
        e.preventDefault();
        setShortcutsOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  // Close on route change
  const openPathRef = useRef<string | null>(null);
  useEffect(() => {
    if (open) {
      if (openPathRef.current == null) openPathRef.current = location.pathname;
      else if (openPathRef.current !== location.pathname) {
        setOpenState(false);
        setQuery('');
        setModeState('find');
        openPathRef.current = null;
      }
    } else {
      openPathRef.current = null;
    }
  }, [open, location.pathname]);

  // Register with the shared overlay stack so Esc closes only the top overlay
  // (palette and Assistant slide-over can coexist — see §20.B acceptance).
  useEffect(() => {
    if (open) {
      registerOverlay(PALETTE_OVERLAY_ID, () => setOpenState(false));
      return () => unregisterOverlay(PALETTE_OVERLAY_ID);
    }
    return undefined;
  }, [open]);

  // Restore focus to opener element when palette closes
  useEffect(() => {
    if (open) return;
    const el = openerRef.current;
    if (el && typeof el.focus === 'function') {
      window.setTimeout(() => el.focus(), 0);
    }
  }, [open]);

  const value = useMemo<CommandPaletteContextValue>(
    () => ({ open, query, mode, setOpen, setQuery, setMode, toggle, openNewSearch }),
    [open, query, mode, setOpen, setQuery, setMode, toggle, openNewSearch],
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandPalette
        open={open}
        onClose={() => setOpen(false)}
        query={query}
        setQuery={setQuery}
      />
      <KeyboardShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </CommandPaletteContext.Provider>
  );
}
